import { useCallback, useEffect, useRef, useState } from "react";
import AdminDataTable from "@/components/admin/AdminDataTable";
import {
  AdminSearchForm,
  AdminSelectFilter,
  AdminTableToolbar,
} from "@/components/admin/AdminTableToolbar";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import {
  getRagSyncErrorMessage,
  getRagSyncLogs,
  retryRagSyncLog,
  syncAllRagTargets,
  type RagBulkSyncTarget,
  type RagSyncEvent,
  type RagSyncLog,
  type RagSyncResult,
  type RagSyncTargetType,
} from "@/services/ragSyncLogs";

const TARGET_TYPES: RagSyncTargetType[] = [
  "SourcePathAsk",
  "SourcePathStudy",
  "DocCategory",
  "DocTag",
];
const SYNC_EVENTS: RagSyncEvent[] = ["Create", "Updated", "Delete"];
const SYNC_RESULTS: RagSyncResult[] = ["Succeeded", "Failed"];
const BULK_SYNC_ACTIONS: Array<{ target: RagBulkSyncTarget; label: string }> = [
  { target: "categories", label: "Sync Categories" },
  { target: "tags", label: "Sync Tags" },
  { target: "sources", label: "Sync Sources" },
];

function splitDateTime(value: string) {
  const [date = "-", time = ""] = value.split(" ");
  return { date, time: time.slice(0, 5) };
}

function formatPayload(value: string | null, emptyText: string) {
  if (!value) return emptyText;
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function parseApiUrls(value: string | null) {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is string => typeof item === "string" && item.trim().length > 0,
      );
    }
    return typeof parsed === "string" && parsed.trim() ? [parsed] : [];
  } catch {
    return [value];
  }
}

export function SyncDetailModal({ log, onClose }: { log: RagSyncLog; onClose: () => void }) {
  const apiUrls = parseApiUrls(log.apiUrl);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="mx-4 flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-sm"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">RAG Sync Detail</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <div className="mb-2 text-xs font-medium text-gray-500">RAG API URL</div>
            <div className="space-y-1.5 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
              {apiUrls.length > 0 ? (
                apiUrls.map((apiUrl) => (
                  <div
                    key={apiUrl}
                    className="break-all font-mono text-xs leading-relaxed text-gray-700"
                  >
                    {apiUrl}
                  </div>
                ))
              ) : (
                <div className="text-xs text-gray-400">No API URL recorded.</div>
              )}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium text-gray-500">SyncRequest</div>
            <pre className="max-h-[280px] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-950 p-4 font-mono text-xs leading-relaxed text-gray-100">
              {formatPayload(log.syncRequest, "No request body.")}
            </pre>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium text-gray-500">SyncResp</div>
            <pre className="max-h-[280px] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-950 p-4 font-mono text-xs leading-relaxed text-gray-100">
              {formatPayload(log.syncResp, "No response body.")}
            </pre>
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function SyncActionButtons({
  log,
  retrying,
  onView,
  onRetry,
}: {
  log: RagSyncLog;
  retrying: boolean;
  onView: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={onView}
        aria-label="View request and response"
        title="View request and response"
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-[#830051]"
      >
        <i className="ri-eye-line text-base" aria-hidden="true" />
      </button>
      {log.syncResult === "Failed" && (
        <button
          type="button"
          disabled={retrying}
          onClick={onRetry}
          aria-label={retrying ? "Retrying synchronization" : "Retry synchronization"}
          title={retrying ? "Retrying synchronization" : "Retry synchronization"}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#830051] transition-colors hover:bg-[#830051]/10 disabled:cursor-wait disabled:opacity-50"
        >
          <i
            className={`${retrying ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} text-base`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

export function BulkSyncButtons({
  syncingTarget,
  onSync,
}: {
  syncingTarget: RagBulkSyncTarget | null;
  onSync: (target: RagBulkSyncTarget) => void;
}) {
  const disabled = syncingTarget !== null;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {BULK_SYNC_ACTIONS.map(({ target, label }) => {
        const syncing = syncingTarget === target;
        return (
          <button
            key={target}
            type="button"
            disabled={disabled}
            aria-label={syncing ? `${label} in progress` : label}
            aria-busy={syncing}
            onClick={() => onSync(target)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-[#830051]/30 hover:bg-[#830051]/5 hover:text-[#830051] disabled:cursor-wait disabled:opacity-50"
          >
            <i
              className={`${syncing ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} text-sm`}
              aria-hidden="true"
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function RagSyncLogsPage() {
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [targetType, setTargetType] = useState<RagSyncTargetType | "">("");
  const [syncEvent, setSyncEvent] = useState<RagSyncEvent | "">("");
  const [syncResult, setSyncResult] = useState<RagSyncResult | "">("");
  const [logs, setLogs] = useState<RagSyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState<RagSyncLog | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [syncingTarget, setSyncingTarget] = useState<RagBulkSyncTarget | null>(null);
  const [notice, setNotice] = useState<{
    message: string;
    kind: "success" | "error";
  } | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPage(1);
  }, [search, targetType, syncEvent, syncResult]);

  // 是否存在生效的筛选/搜索条件，用于控制 Reset 按钮显示
  const hasActiveFilters =
    search !== "" ||
    searchInput !== "" ||
    targetType !== "" ||
    syncEvent !== "" ||
    syncResult !== "";

  // 提交搜索：将草稿写入正式搜索状态
  const handleSearch = () => {
    setSearch(searchInput);
  };

  // 重置全部筛选与搜索条件
  const handleReset = () => {
    setSearch("");
    setSearchInput("");
    setTargetType("");
    setSyncEvent("");
    setSyncResult("");
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getRagSyncLogs({
        search,
        targetType,
        syncEvent,
        syncResult,
        page,
        pageSize,
      });
      setLogs(result.data);
      setTotal(result.total);
    } catch (requestError) {
      setLogs([]);
      setTotal(0);
      setError(getRagSyncErrorMessage(requestError, "Failed to load RAG sync logs."));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, syncEvent, syncResult, targetType]);

  useEffect(() => {
    const timer = window.setTimeout(() => void fetchLogs(), 300);
    return () => window.clearTimeout(timer);
  }, [fetchLogs]);

  useEffect(
    () => () => {
      if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    },
    [],
  );

  const showNotice = useCallback((message: string, kind: "success" | "error") => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    setNotice({ message, kind });
    noticeTimer.current = window.setTimeout(() => {
      setNotice(null);
      noticeTimer.current = null;
    }, 4000);
  }, []);

  const handleRetry = async (log: RagSyncLog) => {
    setRetryingId(log.id);
    setError("");
    try {
      await retryRagSyncLog(log.id);
      await fetchLogs();
    } catch (requestError) {
      setError(getRagSyncErrorMessage(requestError, "Failed to retry RAG synchronization."));
    } finally {
      setRetryingId(null);
    }
  };

  const handleBulkSync = async (target: RagBulkSyncTarget) => {
    if (syncingTarget !== null) return;

    const label = BULK_SYNC_ACTIONS.find((action) => action.target === target)?.label ?? "RAG";
    setSyncingTarget(target);
    setError("");
    try {
      const result = await syncAllRagTargets(target);
      if (result.total === 0) {
        showNotice(`${label} completed: no records found.`, "success");
      } else if (result.failed === 0) {
        showNotice(`${label} completed: ${result.succeeded} succeeded.`, "success");
      } else {
        showNotice(
          `${label} completed: ${result.succeeded} succeeded, ${result.failed} failed. Review the new log entries.`,
          "error",
        );
      }

      if (page === 1) {
        await fetchLogs();
      } else {
        setPage(1);
      }
    } catch (requestError) {
      showNotice(getRagSyncErrorMessage(requestError, `${label} failed to start.`), "error");
    } finally {
      setSyncingTarget(null);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div>
          <AdminPageTitle
            title="RAG Sync Logs"
            tooltip="Records General/Study Source, document category, and document tag synchronization attempts."
          />
        </div>
        <BulkSyncButtons
          syncingTarget={syncingTarget}
          onSync={(target) => void handleBulkSync(target)}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <AdminTableToolbar>
        <AdminSelectFilter
          label="Target Type"
          value={targetType}
          onChange={(value) => setTargetType(value as RagSyncTargetType | "")}
          options={[
            { value: "", label: "All Target Types" },
            ...TARGET_TYPES.map((value) => ({ value, label: value })),
          ]}
        />
        <AdminSelectFilter
          label="Sync Event"
          value={syncEvent}
          onChange={(value) => setSyncEvent(value as RagSyncEvent | "")}
          options={[
            { value: "", label: "All Sync Events" },
            ...SYNC_EVENTS.map((value) => ({ value, label: value })),
          ]}
        />
        <AdminSelectFilter
          label="Sync Result"
          value={syncResult}
          onChange={(value) => setSyncResult(value as RagSyncResult | "")}
          options={[
            { value: "", label: "All Sync Results" },
            ...SYNC_RESULTS.map((value) => ({ value, label: value })),
          ]}
        />
        <AdminSearchForm
          value={searchInput}
          onChange={setSearchInput}
          onSearch={handleSearch}
          searchLabel="Search"
          placeholder="Search by log ID, target ID, API URL, request, or response..."
          showReset={hasActiveFilters}
          onReset={handleReset}
          resetLabel="Reset"
        />
      </AdminTableToolbar>

      <AdminDataTable
        headers={["Sync Time", "Target Type", "Target ID", "Sync Event", "Sync Result", "Action"]}
        columnWidths={["16%", "16%", "24%", "14%", "14%", "16%"]}
        colSpan={6}
        loading={loading}
        empty={logs.length === 0}
        emptyText="No RAG sync logs"
        emptyHint="Synchronization attempts will appear here."
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      >
        {logs.map((log) => {
          const { date, time } = splitDateTime(log.syncTime);
          const retrying = retryingId === log.id;
          return (
            <tr
              key={log.id}
              className="border-b border-gray-50 transition-colors hover:bg-gray-50/60"
            >
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-semibold text-gray-900">{date}</div>
                <div className="mt-0.5 text-xs text-gray-400">{time}</div>
              </td>
              <td className="px-4 py-3 text-xs font-medium text-gray-700">{log.targetType}</td>
              <td className="truncate px-4 py-3 text-[11px] text-gray-500" title={log.targetId}>
                {log.targetId}
              </td>
              <td className="px-4 py-3 text-xs text-gray-600">{log.syncEvent}</td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    log.syncResult === "Succeeded"
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {log.syncResult}
                </span>
              </td>
              <td className="px-4 py-3">
                <SyncActionButtons
                  log={log}
                  retrying={retrying}
                  onView={() => setSelectedLog(log)}
                  onRetry={() => void handleRetry(log)}
                />
              </td>
            </tr>
          );
        })}
      </AdminDataTable>

      {selectedLog && <SyncDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

      {notice && (
        <div
          role={notice.kind === "error" ? "alert" : "status"}
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg ${
            notice.kind === "error" ? "bg-red-600" : "bg-gray-800"
          }`}
        >
          <i
            className={
              notice.kind === "error"
                ? "ri-error-warning-line"
                : "ri-checkbox-circle-line text-green-400"
            }
            aria-hidden="true"
          />
          {notice.message}
        </div>
      )}
    </AdminLayout>
  );
}
